import PageHeading from "../../../components/desktop/PageHeading";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import { api } from "../../../utils/api";
import "./Gracetimemaster.css";

const initialForm = {
  in_time_grace: "",
  out_time_grace: "",
  is_tardy_enabled: false,
};

// Pull a readable message out of an api.js error (FastAPI returns `detail`)
const getErrorMessage = (error, fallback) => {
  const data = error.responseData;
  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && data.detail[0]?.msg) {
      return data.detail[0].msg;
    }
  }
  return fallback;
};

export default function Gracetimemaster() {
  const { showLoader, hideLoader } = useLoaderStore();
  const { user, isAuthenticated } = useAuthStore();
  const companyId = user?.company_id;

  const [form, setForm] = useState(initialForm);
  // Last values saved on the server; Cancel reverts to these
  const [savedForm, setSavedForm] = useState(initialForm);
  // Whether the company already has a grace time record (PUT) or not (POST)
  const [hasRecord, setHasRecord] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchGraceTime = async () => {
    try {
      showLoader("master");

      const response = await api.get(`/grace-time/?company_id=${companyId}`);
      const result = await response.json();

      let record = result?.data ?? result;
      if (Array.isArray(record)) record = record[0];

      if (!record || record.in_grace_time == null) {
        setHasRecord(false);
        return;
      }

      const loaded = {
        in_time_grace: String(record.in_grace_time ?? ""),
        out_time_grace: String(record.out_grace_time ?? ""),
        is_tardy_enabled: Boolean(record.tardy_applicable),
      };

      setForm(loaded);
      setSavedForm(loaded);
      setHasRecord(true);
    } catch (error) {
      // 404 means grace time has not been configured for this company yet
      if (error.status === 404) {
        setHasRecord(false);
        return;
      }
      console.error(error);
      toast.error(getErrorMessage(error, "Failed to load grace time"));
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (companyId && isAuthenticated) {
      fetchGraceTime();
    }
  }, [companyId, isAuthenticated]);

  const handleMinutesChange = (field, value) => {
    // Allow only whole numbers
    if (value !== "" && !/^\d+$/.test(value)) return;

    setForm({
      ...form,
      [field]: value,
    });
  };

  const handleTardyChange = (enabled) => {
    setForm({
      ...form,
      is_tardy_enabled: enabled,
    });
  };

  const handleCancel = () => {
    setForm(savedForm);
  };

  const handleSave = async () => {
    if (form.in_time_grace === "") {
      toast.warning("In Time grace is required");
      return;
    }
    if (form.out_time_grace === "") {
      toast.warning("Out Time grace is required");
      return;
    }
    if (!companyId) {
      toast.error("Company not found. Please login again.");
      return;
    }

    try {
      setSaving(true);
      showLoader("master");

      const payload = {
        in_grace_time: Number(form.in_time_grace),
        out_grace_time: Number(form.out_time_grace),
        tardy_applicable: form.is_tardy_enabled,
      };

      if (hasRecord) {
        await api.put(`/grace-time/?company_id=${companyId}`, payload);
      } else {
        try {
          await api.post(`/grace-time/`, { company_id: companyId, ...payload });
        } catch (error) {
          // Record already exists for this company, update it instead
          if (error.status !== 400 && error.status !== 409) throw error;
          await api.put(`/grace-time/?company_id=${companyId}`, payload);
        }
        setHasRecord(true);
      }

      setSavedForm(form);
      toast.success(
        `Grace time ${hasRecord ? "updated" : "saved"} successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error, "Failed to save grace time"));
    } finally {
      setSaving(false);
      hideLoader();
    }
  };

  return (
    <div className="grace-time-master-container">
      <PageHeading>Grace Time</PageHeading>

      <div className="grace-time-form">
        <div className="grace-time-field-group">
          <label>
            In Time (Minutes) <span>*</span>
          </label>
          <input
            type="number"
            min="0"
            value={form.in_time_grace}
            onChange={(e) =>
              handleMinutesChange("in_time_grace", e.target.value)
            }
            placeholder="Enter In Time Grace in Minutes"
          />
        </div>

        <div className="grace-time-field-group">
          <label>
            Out Time (Minutes) <span>*</span>
          </label>
          <input
            type="number"
            min="0"
            value={form.out_time_grace}
            onChange={(e) =>
              handleMinutesChange("out_time_grace", e.target.value)
            }
            placeholder="Enter Out Time Grace in Minutes"
          />
        </div>

        <div className="grace-time-field-group">
          <div className="grace-time-tardy-toggle-row">
            <label>Tardy</label>
            <div className="grace-time-tardy-options">
              <button
                type="button"
                className={`grace-time-tardy-btn grace-time-tardy-apply ${
                  form.is_tardy_enabled ? "active" : ""
                }`}
                onClick={() => handleTardyChange(true)}
              >
                Apply
              </button>
              <button
                type="button"
                className={`grace-time-tardy-btn grace-time-tardy-dont-apply ${
                  !form.is_tardy_enabled ? "active" : ""
                }`}
                onClick={() => handleTardyChange(false)}
              >
                Don't Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grace-time-form-actions">
        <button className="grace-time-cancel-btn" onClick={handleCancel}>
          Cancel
        </button>

        <button
          className="grace-time-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          Save
        </button>
      </div>
    </div>
  );
}
